import React, { useState, useEffect, useCallback } from 'react';
import { FiClock, FiMapPin, FiExternalLink } from 'react-icons/fi';
import { useNavigate } from 'react-router-dom';
import { fetchTickets, updateTicket, updateMyLocation } from '../api/api';
import { useSocket } from '../context/SocketContext';
import './EngineerDashboard.css';

const EngineerDashboard = () => {
  const socket = useSocket();
  const navigate = useNavigate();
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [updateStatus, setUpdateStatus] = useState('in-progress');
  const [resolution, setResolution] = useState('');
  const [updating, setUpdating] = useState(false);
  const [updatingLocation, setUpdatingLocation] = useState(false);

  const goToTicketDetails = (ticketId) => {
    navigate(`/engineer/tickets/${ticketId}`);
  };

  const handleUpdateLocation = () => {
    if (!navigator.geolocation) {
      alert('Location is not supported by your browser');
      return;
    }
    setUpdatingLocation(true);
    navigator.geolocation.getCurrentPosition(
      async (pos) => {
        try {
          await updateMyLocation(pos.coords.latitude, pos.coords.longitude);
          alert('Location updated. You will receive complaints near you.');
        } catch (err) {
          alert(err.message || 'Failed to update location');
        } finally {
          setUpdatingLocation(false);
        }
      },
      () => {
        alert('Could not get your location. Please allow location access.');
        setUpdatingLocation(false);
      },
      { enableHighAccuracy: true, timeout: 15000, maximumAge: 10000 }
    );
  };

  const loadTickets = useCallback(() => {
    setLoading(true);
    fetchTickets()
      .then((data) => setTickets(Array.isArray(data) ? data : []))
      .catch(() => setTickets([]))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    loadTickets();
  }, [loadTickets]);

  useEffect(() => {
    if (!socket) return;
    const onAssigned = () => loadTickets();
    const onUpdated = () => loadTickets();
    socket.on('ticket:assigned', onAssigned);
    socket.on('ticket:updated', onUpdated);
    return () => {
      socket.off('ticket:assigned', onAssigned);
      socket.off('ticket:updated', onUpdated);
    };
  }, [socket, loadTickets]);

  const openUpdate = (ticket) => {
    setSelectedTicket(ticket);
    setUpdateStatus(ticket.status || 'in-progress');
    setResolution(ticket.resolution || '');
    setShowModal(true);
  };

  const handleUpdate = async (e) => {
    e.preventDefault();
    if (!selectedTicket) return;
    if (updateStatus === 'resolved' && !resolution.trim()) {
      alert('Please provide resolution details');
      return;
    }
    setUpdating(true);
    try {
      await updateTicket(selectedTicket.id, {
        status: updateStatus,
        resolution: updateStatus === 'resolved' ? resolution.trim() : undefined,
      });
      setTickets((prev) =>
        prev.map((t) =>
          String(t.id) === String(selectedTicket.id)
            ? { ...t, status: updateStatus, resolution: updateStatus === 'resolved' ? resolution : t.resolution }
            : t
        )
      );
      setShowModal(false);
      setSelectedTicket(null);
    } catch (err) {
      alert(err.message || 'Failed to update ticket');
    } finally {
      setUpdating(false);
    }
  };

  const getStatusColor = (status) => {
    const s = (status || '').toLowerCase();
    if (s === 'open') return '#FF6B6B';
    if (s === 'in-progress') return '#FFA500';
    if (s === 'resolved' || s === 'closed') return '#4ECDC4';
    return '#999';
  };

  const getStatusLabel = (status) => {
    const s = (status || '').toLowerCase();
    if (s === 'open') return 'Pending';
    if (s === 'in-progress') return 'In Progress';
    if (s === 'resolved' || s === 'closed') return 'Completed';
    return status || 'Pending';
  };

  const getPriorityColor = (p) => {
    const pr = (p || 'medium').toLowerCase();
    if (pr === 'high') return '#FF6B6B';
    if (pr === 'medium') return '#FFA500';
    if (pr === 'low') return '#4ECDC4';
    return '#999';
  };

  return (
    <div className="engineer-dashboard">
      <div className="engineer-header">
        <div>
          <h1>Assigned Tickets</h1>
          <p>Update status and resolution for your tickets</p>
        </div>
        <button
          type="button"
          className="engineer-update-location-btn"
          onClick={handleUpdateLocation}
          disabled={updatingLocation}
        >
          <FiMapPin /> {updatingLocation ? 'Updating...' : 'Update Location'}
        </button>
      </div>

      {loading ? (
        <p className="engineer-loading">Loading...</p>
      ) : tickets.length === 0 ? (
        <p className="engineer-empty">No assigned tickets.</p>
      ) : (
        <div className="engineer-tickets">
          {tickets.map((t) => (
            <div
              key={t.id}
              className="engineer-ticket-card"
              onClick={() => openUpdate(t)}
              onKeyDown={(e) => e.key === 'Enter' && openUpdate(t)}
              role="button"
              tabIndex={0}
            >
              <div className="engineer-ticket-header">
                <h3>{t.title}</h3>
                <span className="engineer-priority" style={{ background: getPriorityColor(t.priority) }}>
                  {(t.priority || 'medium').toUpperCase()}
                </span>
              </div>
              <p className="engineer-ticket-desc">{t.description?.slice(0, 100)}{t.description?.length > 100 ? '...' : ''}</p>
              <div className="engineer-ticket-footer">
                <span className="engineer-status" style={{ background: getStatusColor(t.status) }}>
                  {getStatusLabel(t.status)}
                </span>
                <div className="engineer-ticket-actions">
                  <button 
                    className="engineer-view-details-btn"
                    onClick={(e) => {
                      e.stopPropagation();
                      goToTicketDetails(t.id);
                    }}
                    title="View full details"
                  >
                    <FiExternalLink /> Details
                  </button>
                  <span className="engineer-date">
                    <FiClock /> {t.created_at ? new Date(t.created_at).toLocaleDateString() : '—'}
                  </span>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {showModal && selectedTicket && (
        <div className="engineer-modal-overlay" onClick={() => !updating && setShowModal(false)}>
          <div className="engineer-modal" onClick={(e) => e.stopPropagation()}>
            <h3>Update Ticket</h3>
            <p className="engineer-modal-title">{selectedTicket.title}</p>
            <form onSubmit={handleUpdate}>
              <div className="engineer-form-group">
                <label>Status</label>
                <select value={updateStatus} onChange={(e) => setUpdateStatus(e.target.value)}>
                  <option value="open">Pending</option>
                  <option value="in-progress">In Progress</option>
                  <option value="resolved">Completed</option>
                </select>
              </div>
              {updateStatus === 'resolved' && (
                <div className="engineer-form-group">
                  <label>Resolution *</label>
                  <textarea
                    value={resolution}
                    onChange={(e) => setResolution(e.target.value)}
                    placeholder="Describe the resolution..."
                    rows={4}
                    required
                  />
                </div>
              )}
              <div className="engineer-modal-actions">
                <button type="button" onClick={() => !updating && setShowModal(false)} disabled={updating}>Cancel</button>
                <button type="submit" disabled={updating}>{updating ? 'Updating...' : 'Update'}</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default EngineerDashboard;
